import type { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import { useEffect } from 'react';
import Pool from '@/services/auth/userPool';
import { fetchUserSettings, isSubscriptionExpired } from '@/services/auth/userSettingsApi';
import { useAuthStore } from '@/stores/slices/useAuthStore';

export const useSessionInitialization = (): void => {
  const setUser = useAuthStore((state) => state.setUser);
  const setIsInitializing = useAuthStore((state) => state.setIsInitializing);
  const getCachedSession = useAuthStore((state) => state.getCachedSession);
  const isSessionCacheValid = useAuthStore((state) => state.isSessionCacheValid);
  const clearSessionCache = useAuthStore((state) => state.clearSessionCache);

  useEffect(() => {
    const initSession = async (): Promise<void> => {
      try {
        if (isSessionCacheValid()) {
          const cached = getCachedSession();
          if (cached?.user) {
            setUser(cached.user);
            setIsInitializing(false);
            return;
          }
        }

        const current = Pool.getCurrentUser() as CognitoUser | null;
        if (!current) {
          clearSessionCache();
          setIsInitializing(false);
          return;
        }

        current.getSession((err: Error | null, session: CognitoUserSession | null) => {
          if (err || !session || !session.isValid()) {
            current.signOut();
            clearSessionCache();
            setUser(null);
            setIsInitializing(false);
            return;
          }

          const idToken = session.getIdToken();
          const accessToken = session.getAccessToken();
          const refreshToken = session.getRefreshToken();
          const token = idToken.getJwtToken();

          fetchUserSettings(token)
            .then((data) => {
              const payload = idToken.payload || {};
              const userName = (payload.name as string) || (payload['cognito:username'] as string) || '';

              const userData = {
                idToken: {
                  jwtToken: idToken.getJwtToken(),
                  payload: {
                    name: userName,
                    ...payload,
                  },
                },
                accessToken: {
                  jwtToken: accessToken.getJwtToken(),
                  payload: accessToken.payload,
                },
                refreshToken: refreshToken
                  ? {
                      token: refreshToken.getToken(),
                    }
                  : undefined,
                config: {
                  ...data.settings,
                  subscription_type: data.subscription_type,
                  expiration_timestamp: data.expiration_timestamp,
                  customer_id: data.customer_id,
                },
              };

              if (userData.config.subscription_type === 'free') {
                if (isSubscriptionExpired(userData.config.expiration_timestamp)) {
                  alert('Your trial account has expired');
                  current.signOut();
                  clearSessionCache();
                  setUser(null);
                } else {
                  setUser(userData);
                }
              } else {
                setUser(userData);
              }
            })
            .catch((error) => {
              console.error('Failed to fetch settings:', error);
              current.signOut();
              clearSessionCache();
              setUser(null);
            })
            .finally(() => {
              setIsInitializing(false);
            });
        });
      } catch (error) {
        console.error('Session init error:', error);
        clearSessionCache();
        setIsInitializing(false);
      }
    };

    initSession();
  }, [setUser, setIsInitializing, getCachedSession, isSessionCacheValid, clearSessionCache]);
};
